import { useCallback, useEffect, useState } from "react";

import { SummaryTable } from "./summary_table";

import { ErrorMessage } from "components/shared/error_message";
import { TLD } from "config";
import { FlightAPI } from "contexts/flight_api_context";
import { ForumAPI } from "contexts/forum_api_context";
import { useInterval } from "hooks/use_interval";
import { ClassMonitoringInfoType, FlightServerType } from "shared/types";
import { getQueryParam } from "utils/fetch";

function Monitoring() {
  const dateString = getQueryParam("date") as string;
  const [flightServers, setFlightServers] = useState<FlightServerType[]>([]);
  const [classes, setClasses] = useState<ClassMonitoringInfoType[]>([]);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const fetchMonitoringData = useCallback(
    async (servers: FlightServerType[]) => {
      if (!errorMessage && servers.length > 0) {
        try {
          const monitoringServers = servers.filter(
            ({ is_available, is_monitoring_server }) => is_available && is_monitoring_server,
          );
          const allMonitoringData = await Promise.all(
            monitoringServers.map(({ hostname_prefix }) => {
              const baseUrl = `https://${hostname_prefix}.${TLD}`;
              const flightAPI = new FlightAPI(baseUrl, "");
              return flightAPI.getMonitoringData(dateString);
            }),
          );
          const allClasses = allMonitoringData.flat();
          allClasses.sort((a, b) => (!a.date ? -1 : !b.date ? 1 : a.date - b.date));
          setClasses(allClasses);
        } catch (error) {
          console.error(error);
          setErrorMessage((error as Error).message);
        }
      }
    },
    [errorMessage, dateString],
  );

  useEffect(() => {
    async function fetchFlightServers() {
      const forumAPI = new ForumAPI();
      const response = await forumAPI.get(`api/v1/flight/flightservers`);
      if (response.ok) {
        const servers = (await response.json()) as FlightServerType[];
        setFlightServers(servers);
      }
    }
    fetchFlightServers();
  }, []);

  useEffect(() => {
    if (!flightServers) return;
    fetchMonitoringData(flightServers);
  }, [flightServers, fetchMonitoringData]);

  // Poll data every minute
  useInterval(() => {
    if (dateString) return;
    fetchMonitoringData(flightServers);
  }, 60000);

  useEffect(() => {
    const title = `Flight | Monitoring Dashboard`;
    document.title = title;
    const meta = document.querySelector<HTMLMetaElement>("meta[name='description']");
    if (meta) {
      meta.content = title;
    }
  }, []);

  if (errorMessage) {
    return <ErrorMessage message={errorMessage} />;
  }

  return (
    <div className="vertical-scroll" style={{ height: "100vh" }}>
      <div className="position-relative bg-blue">
        <div className="h1 py4 mx-auto bg-blue text-white mb0 center">
          Flight Monitoring Dashboard
        </div>
      </div>
      <div className="p6">
        <SummaryTable classes={classes} />
      </div>
    </div>
  );
}

export { Monitoring };
